import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ShoppingCart, Plus, Minus, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { MolecularBg } from "../components/MolecularBg";
import { useCart } from "../context/CartContext";
import { useSEO } from "../lib/useSEO";
import api from "../lib/api";

export const StackBuilderPage = () => {
    const [products, setProducts] = useState([]);
    const [selected, setSelected] = useState([]);
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(false);
    const { addItem, setPromoCode } = useCart();
    const navigate = useNavigate();

    useSEO({
        title: "Build Your Own Stack — Carbon Labs Research Peptides",
        description: "Build your own research peptide stack with automatic tiered discounts: 5% on 2 items, 10% on 3, 15% on 4+.",
    });

    useEffect(() => {
        api.get("/products").then((r) => setProducts(r.data));
    }, []);

    const isSelected = (slug) => selected.includes(slug);

    const toggle = (slug) => {
        setSelected((prev) =>
            prev.includes(slug) ? prev.filter((s) => s !== slug) : prev.length < 8 ? [...prev, slug] : prev
        );
    };

    // Fetch quote whenever selection changes
    useEffect(() => {
        if (selected.length < 2) { setQuote(null); return; }
        let cancelled = false;
        api.post("/stack-builder/quote", { slugs: selected }).then((r) => {
            if (!cancelled) setQuote(r.data);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [selected]);

    const checkout = async () => {
        if (selected.length < 2) {
            toast.error("Select at least 2 compounds to build a stack");
            return;
        }
        setLoading(true);
        try {
            const res = await api.post("/stack-builder/apply", { slugs: selected });
            // Add all to cart, skipping out-of-stock items defensively
            let skipped = 0;
            res.data.products.forEach((p) => {
                const r = addItem(p, 1);
                if (r && r.ok === false) skipped += 1;
            });
            setPromoCode(res.data.code);
            if (skipped === 0) {
                toast.success(`Stack added with ${res.data.discount_pct}% discount applied`);
            } else {
                toast.warning(`Stack added with ${res.data.discount_pct}% off — ${skipped} out-of-stock item(s) were skipped`);
            }
            navigate("/cart");
        } catch (err) {
            toast.error("Could not build stack. Try again.");
        }
        setLoading(false);
    };

    const grouped = useMemo(() => {
        const g = {};
        products.forEach((p) => {
            g[p.category] = g[p.category] || [];
            g[p.category].push(p);
        });
        return g;
    }, [products]);

    return (
        <div className="pt-24 pb-24 min-h-screen relative" data-testid="stack-builder-page">
            <MolecularBg intensity="low" />
            <div className="container-x relative z-10">
                <span className="eyebrow">// Custom Protocol</span>
                <h1 className="font-display italic text-4xl md:text-5xl font-black text-white tracking-tighter mt-1 mb-3 uppercase">
                    Build Your Own Stack
                </h1>
                <p className="text-[var(--text-secondary)] mb-6 max-w-2xl">
                    Select 2 or more compounds to build a custom research stack. Tiered discount applied automatically.
                </p>

                {/* Discount tiers */}
                <div className="glass rounded-2xl p-4 mb-8 flex flex-wrap items-center gap-4 md:gap-6 text-sm" data-testid="discount-tiers">
                    <span className="text-[var(--text-muted)] font-mono uppercase tracking-widest text-xs">Discount Tiers:</span>
                    {[{n: 2, p: 5}, {n: 3, p: 10}, {n: 4, p: 15}].map((t) => (
                        <span key={t.n} className={`inline-flex items-center gap-1.5 ${quote && quote.items_count >= t.n ? "text-[var(--gold)]" : "text-[var(--text-secondary)]"}`}>
                            <span className="font-mono">{t.n}{t.n === 4 ? "+" : ""} items</span>
                            <ArrowRight size={11} />
                            <span className="font-bold">{t.p}% off</span>
                        </span>
                    ))}
                </div>

                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Product selector */}
                    <div className="lg:col-span-2 space-y-6">
                        {Object.entries(grouped).map(([cat, prods]) => (
                            <div key={cat}>
                                <h3 className="eyebrow mb-3 capitalize">// {cat.replace("-", " ")}</h3>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    {prods.map((p) => {
                                        const sel = isSelected(p.slug);
                                        return (
                                            <button
                                                key={p.slug}
                                                onClick={() => toggle(p.slug)}
                                                data-testid={`builder-item-${p.slug}`}
                                                className={`text-left p-3 rounded-2xl border-2 transition-all flex items-center gap-3 group ${
                                                    sel
                                                        ? "border-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                                        : "border-white/8 glass hover:border-[rgba(233,188,11,0.3)]"
                                                }`}
                                            >
                                                <div className="w-20 h-14 rounded-lg overflow-hidden flex-shrink-0">
                                                    <img src={p.image} alt={p.name} className="w-full h-full object-contain" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5 mb-0.5">
                                                        <span className="font-display italic uppercase font-bold text-sm text-white truncate">{p.name}</span>
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{p.dosage}</span>
                                                        <span className="font-mono text-sm text-white">A${p.price.toFixed(2)}</span>
                                                    </div>
                                                </div>
                                                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                                                    sel ? "bg-[var(--gold)] text-black" : "border border-white/15 text-white/40"
                                                }`}>
                                                    {sel ? <Check size={14} /> : <Plus size={14} />}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Summary panel */}
                    <div className="lg:col-span-1">
                        <div className="glass-elevated rounded-2xl p-6 sticky top-24 neon-border-cyan" data-testid="stack-builder-summary">
                            <h3 className="font-display italic uppercase font-bold text-white mb-3">Your Stack</h3>

                            {selected.length === 0 ? (
                                <p className="text-sm text-[var(--text-secondary)] py-4">Select compounds to start building.</p>
                            ) : (
                                <>
                                    <ul className="space-y-1.5 mb-4 text-sm max-h-64 overflow-y-auto pr-1">
                                        {quote?.products.map((p) => (
                                            <li key={p.slug} className="flex items-center justify-between gap-2">
                                                <span className="text-[var(--text-secondary)] truncate">{p.name}</span>
                                                <button
                                                    onClick={() => toggle(p.slug)}
                                                    className="text-[var(--text-muted)] hover:text-red-400 flex-shrink-0"
                                                    aria-label="Remove"
                                                >
                                                    <Minus size={12} />
                                                </button>
                                            </li>
                                        )) || selected.map((s) => (
                                            <li key={s} className="text-[var(--text-secondary)] truncate">{s}</li>
                                        ))}
                                    </ul>

                                    {quote && (
                                        <div className="space-y-1.5 text-sm border-t border-white/5 pt-3">
                                            <div className="flex justify-between text-[var(--text-secondary)]">
                                                <span>Subtotal</span>
                                                <span className="font-mono">A${quote.subtotal.toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between text-emerald-300" data-testid="builder-discount-line">
                                                <span>Discount ({quote.discount_pct}%)</span>
                                                <span className="font-mono">−A${quote.discount_amount.toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between font-display text-lg font-bold text-white pt-2 border-t border-white/5">
                                                <span>You pay</span>
                                                <span className="text-[var(--gold)]" data-testid="builder-total">A${quote.total.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    )}

                                    {selected.length === 1 && (
                                        <p className="text-xs text-amber-300 mt-3" data-testid="builder-min-warning">
                                            Add one more compound to unlock 5% discount.
                                        </p>
                                    )}
                                </>
                            )}

                            <button
                                onClick={checkout}
                                disabled={selected.length < 2 || loading}
                                className="btn-solid w-full mt-5 pulse-glow disabled:opacity-40 disabled:cursor-not-allowed"
                                data-testid="builder-checkout-button"
                            >
                                {loading ? "Adding..." : <><Sparkles size={14} /> Add Stack & Save</>}
                            </button>

                            <Link to="/stacks" className="block text-center text-xs text-[var(--text-muted)] hover:text-[var(--gold)] mt-3 font-mono uppercase tracking-widest">
                                Or browse curated stacks
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
